// ===========================================================================
// Simulated DeepSORT-like Tracker
// Maintains stable track IDs across frames using nearest-neighbor association
// ===========================================================================

import { Detection, Track, TrackerState } from '../types';

const HISTORY_MAX_LENGTH = 50;

/**
 * Create initial tracker state
 */
export function createTrackerState(maxAge: number = 30, minHits: number = 3): TrackerState {
  return {
    tracks: new Map(),
    nextTrackId: 1,
    maxAge,
    minHits,
  };
}

/**
 * Run tracker update with new detections
 * Returns updated tracker state and current tracks array
 */
export function updateTracker(
  state: TrackerState,
  detections: Detection[],
  frameIndex: number,
  maxAge: number,
  minHits: number
): { state: TrackerState; tracks: Track[] } {
  // Filter person detections only
  const personDetections = detections.filter((d) => d.cls === 'person');

  // Copy tracks map for modification
  const tracks = new Map(state.tracks);
  let nextTrackId = state.nextTrackId;

  // Mark all tracks as unmatched initially
  const unmatchedTracks = new Set(tracks.keys());
  const unmatchedDetections = new Set(personDetections.map((_, i) => i));

  // Association: greedy nearest-neighbor by centroid distance
  const associations: Array<{ trackId: number; detIdx: number; dist: number }> = [];

  for (const [trackId, track] of tracks) {
    for (let i = 0; i < personDetections.length; i++) {
      const det = personDetections[i];
      const dist = euclideanDistance(track.cx, track.cy, det.cx, det.cy);
      associations.push({ trackId, detIdx: i, dist });
    }
  }

  // Sort by distance
  associations.sort((a, b) => a.dist - b.dist);

  // Greedy matching with distance threshold
  const distThreshold = 100; // pixels
  const matched = new Set<string>();

  for (const { trackId, detIdx, dist } of associations) {
    if (dist > distThreshold) break;

    if (matched.has(`t-${trackId}`) || matched.has(`d-${detIdx}`)) continue;

    matched.add(`t-${trackId}`);
    matched.add(`d-${detIdx}`);
    unmatchedTracks.delete(trackId);
    unmatchedDetections.delete(detIdx);

    // Update matched track
    const track = tracks.get(trackId)!;
    const det = personDetections[detIdx];

    const updatedTrack: Track = {
      ...track,
      x1: det.x1,
      y1: det.y1,
      x2: det.x2,
      y2: det.y2,
      cx: det.cx,
      cy: det.cy,
      hits: track.hits + 1,
      lastSeen: frameIndex,
      confirmed: track.hits + 1 >= minHits,
      agentId: det.agentId,
      history: [
        ...track.history.slice(-(HISTORY_MAX_LENGTH - 1)),
        { x: det.cx, y: det.cy, frame: frameIndex },
      ],
    };

    tracks.set(trackId, updatedTrack);
  }

  // Age unmatched tracks
  for (const trackId of unmatchedTracks) {
    const track = tracks.get(trackId)!;
    const age = frameIndex - track.lastSeen;

    if (age > maxAge) {
      // Remove stale track
      tracks.delete(trackId);
    } else {
      // Keep but don't update position
      tracks.set(trackId, {
        ...track,
        hits: Math.max(0, track.hits - 1),
      });
    }
  }

  // Create new tracks for unmatched detections
  for (const detIdx of unmatchedDetections) {
    const det = personDetections[detIdx];

    const newTrack: Track = {
      trackId: nextTrackId++,
      label: 'person',
      x1: det.x1,
      y1: det.y1,
      x2: det.x2,
      y2: det.y2,
      cx: det.cx,
      cy: det.cy,
      age: 0,
      hits: 1,
      lastSeen: frameIndex,
      confirmed: 1 >= minHits,
      history: [{ x: det.cx, y: det.cy, frame: frameIndex }],
      agentId: det.agentId,
    };

    tracks.set(newTrack.trackId, newTrack);
  }

  // Update age for all tracks
  for (const [trackId, track] of tracks) {
    tracks.set(trackId, {
      ...track,
      age: track.age + 1,
    });
  }

  return {
    state: { ...state, tracks, nextTrackId, maxAge, minHits },
    tracks: Array.from(tracks.values()),
  };
}

/**
 * Update suspect labels based on weapon proximity
 */
export function updateSuspectLabels(
  tracks: Track[],
  weaponPositions: Array<{ x: number; y: number }>,
  suspectProximity: number,
  suspectConfirmFrames: number,
  suspectPinFrames: number,
  agentSuspectState: Map<string, { nearFrames: number; pinFrames: number }>
): Track[] {
  return tracks.map((track) => {
    const agentId = track.agentId;
    if (!agentId) return track;

    // Get or create suspect state for this agent
    let suspectState = agentSuspectState.get(agentId);
    if (!suspectState) {
      suspectState = { nearFrames: 0, pinFrames: 0 };
      agentSuspectState.set(agentId, suspectState);
    }

    // Check if near any weapon
    const isNearWeapon = weaponPositions.some(
      (w) => euclideanDistance(track.cx, track.cy, w.x, w.y) < suspectProximity
    );

    if (isNearWeapon) {
      suspectState.nearFrames++;
      suspectState.pinFrames = suspectPinFrames;
    } else {
      suspectState.nearFrames = 0;
      if (suspectState.pinFrames > 0) {
        suspectState.pinFrames--;
      }
    }

    // Determine label
    const isSuspect =
      suspectState.nearFrames >= suspectConfirmFrames || suspectState.pinFrames > 0;

    return {
      ...track,
      label: isSuspect ? 'suspect' : 'person',
    };
  });
}

/**
 * Get confirmed tracks only
 */
export function getConfirmedTracks(tracks: Track[]): Track[] {
  return tracks.filter((t) => t.confirmed);
}

/**
 * Reset tracker state
 */
export function resetTrackerState(): TrackerState {
  return createTrackerState();
}

// ----------------------------- Utility Functions ---------------------------

function euclideanDistance(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}
